// packages
import React from 'react';
import { Box } from '@mui/material';
import { gql, DocumentNode } from '@apollo/client';

// logic
import {
	InstallmentStatus,
	InvoiceStatus,
	InvoiceViewAutopayInvoiceFragment,
} from '#types';
import { Button } from '../../../Button';
import styles from './InvoiceViewAutopay.module.scss';
import {
	useInvoiceAutopay,
	useInvoiceAutopayInvoiceFragment,
} from './useInvoiceAutopay';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { InvoiceViewAutoPayForm } from './InvoiceViewAutoPayForm';
import { Spinner } from '../../../Spinner';
import { useStripeTheme } from '../../../../config/StripeTheme/useStripeTheme';
import rpcShared from '@rockpapercoin/rpc-shared';

const stripePromise = loadStripe( process.env.NEXT_PUBLIC_STRIPE_PUB_KEY || '' );

const invoiceViewAutopayInvoiceFragment = gql`
	fragment invoiceViewAutopayInvoice on Invoice {
		id
		status
		paymentInstallments {
			id
			status
			scheduledPaymentDate
			scheduledPaymentMethod
		}
		contact {
			...getViewingUserDocumentRelationshipContact
		}
		organization {
			...invoiceViewAutoPayFormOrganization
		}
		...invoiceViewAutoPayFormInvoice
		...useInvoiceAutopayInvoice
	}
	${ InvoiceViewAutoPayForm.fragments.invoiceViewAutoPayFormOrganizationFragment }
	${ InvoiceViewAutoPayForm.fragments.invoiceViewAutoPayFormInvoiceFragment }
	${ rpcShared.contacts.getViewingUserDocumentRelationshipContactFragment }
	${ useInvoiceAutopayInvoiceFragment }
`;

export type InvoiceViewAutopayProps = {
	invoice?: InvoiceViewAutopayInvoiceFragment;
	requestingUserBaseId?: string;
	guestToken?: string;
};

type InvoiceViewAutopayPropsWithFragment = React.FC<InvoiceViewAutopayProps> & {
	fragments: {
		invoiceViewAutopayInvoiceFragment: DocumentNode;
	};
};

export const InvoiceViewAutopay: InvoiceViewAutopayPropsWithFragment = ( {
	invoice,
	requestingUserBaseId,
	guestToken,
} ) => {
	const autoPayableInvoiceStatuses = [ InvoiceStatus.Sent, InvoiceStatus.PartiallyPaid, ];

	const { viewingUserMayAct: viewingUserIsCustomerOrAPPAWithPaymentPerms } =
		invoice?.contact
			? rpcShared.contacts.getViewingUserDocumentRelationship( {
				contact: invoice.contact,
				viewingUserId: requestingUserBaseId,
				permission: 'plannerOrgCanPayInvoices',
			  } )
			: {
				viewingUserMayAct: false,
			  };

	const shouldRenderAutopay =
		viewingUserIsCustomerOrAPPAWithPaymentPerms &&
		invoice &&
		autoPayableInvoiceStatuses.includes( invoice.status ) &&
		invoice.paymentInstallments?.some(
			( installment ) => installment.status === InstallmentStatus.Unpaid
		);

	const {
		autopayIsActive,
		loading,
		autopayIsExpanded,
		setAutopayIsExpanded,
		stripeElementOptions,
		onDeactivate,
		onActivateOrUpdate,
	} = useInvoiceAutopay( { invoice, requestingUserBaseId, guestToken } );

	const stripeTheme = useStripeTheme();

	return shouldRenderAutopay ? (
		<Box className={ styles.container }>
			<Button
				type='submit'
				variant='outlined'
				className={ styles.showButton }
				data-cy='invoice-autopay-button'
				onClick={ () => setAutopayIsExpanded( !autopayIsExpanded ) }
			>
				Autopay Settings
			</Button>
			{ loading ? (
				<Spinner />
			) : autopayIsExpanded && stripeElementOptions ? (
				<Elements
					stripe={ stripePromise }
					options={ {
						...stripeElementOptions,
						...stripeTheme,
					} }
				>
					<InvoiceViewAutoPayForm
						invoiceId={ invoice?.id }
						invoice={ invoice }
						guestToken={ guestToken }
						autopayIsActive={ autopayIsActive }
						stripeElementOptions={ stripeElementOptions }
						onDeactivate={ onDeactivate }
						onActivateOrUpdate={ onActivateOrUpdate }
						organization={ invoice?.organization }
					/>
				</Elements>
			) : null }
		</Box>
	) : null;
};

InvoiceViewAutopay.fragments = {
	invoiceViewAutopayInvoiceFragment,
};
